import { createContext, useContext, useState } from "react";

const LangCtx = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("ncs_lang") || "fr");
  const setLang = (l) => {
    setLangState(l);
    localStorage.setItem("ncs_lang", l);
  };
  const t = (key) => STRINGS[key]?.[lang] ?? STRINGS[key]?.fr ?? key;
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);

export const AUDIENCE_EN = {
  "Marque / Boutique": "Brand / Boutique",
  "Hôtel / Lieu culturel": "Hotel / Cultural venue",
  "Entreprise / Collectivité": "Company / Public sector",
  "Particulier / Mariage": "Individual / Wedding",
};

export const SERVICE_TITLE_EN = {
  "Customisation": "Customization",
  "Bottle Custom": "Bottle Custom",
  "Objets & Bijoux": "Objects & Jewelry",
  "Feuille d'Or": "Gold Leaf",
  "Peinture sur textile": "Textile Painting",
  "Peinture murale": "Mural Painting",
  "Live Painting": "Live Painting",
  "Workshops": "Workshops",
  "Ateliers Collage": "Collage Workshops",
  "Autre projet sur mesure": "Other / custom project",
};

export const POLE_EN = {
  personnaliser: { name: "Customize", tagline: "Sneakers, bottles, jewelry, leather — every object becomes a one-of-a-kind piece." },
  peindre: { name: "Paint", tagline: "Textile, walls, canvas — art comes alive on every surface." },
  transmettre: { name: "Share", tagline: "Workshops to create, experiment, share." },
};

export const MARQUEE_EN = [
  "CUSTOMIZATION",
  "MURALS",
  "LIVE PAINTING",
  "WORKSHOPS",
  "BOTTLE CUSTOM",
  "GOLD LEAF",
  "TEXTILE PAINTING",
  "OBJECTS & JEWELRY",
  "COLLAGE WORKSHOPS",
];

export const PRESTATION_EN = {
  "customisation": {
    title: "Customization",
    accroche: "One piece, one signature",
    description: "With over 5,000 completed projects, our artists transform sneakers, bags and accessories into one-of-a-kind pieces, hand-painted and personalized. A service designed to create an experience, elevate a product or offer an artistic animation directly to the public.",
    cta: "Request a quote for a customization",
    details: [
      { label: "Mediums", value: "Sneakers · bags · leather · accessories" },
      { label: "Formats", value: "One-of-a-kind piece · client personalization · live animation · brand activation" },
      { label: "For", value: "Brands · boutiques · events · companies · individuals" },
    ],
  },
  "bottle-custom": {
    title: "Bottle Custom",
    accroche: "One bottle, one signature",
    description: "Artistic personalization of bottles, as unique pieces or in series, to create singular event objects. The service can be done beforehand or live on the event site as an artistic animation.",
    cta: "Request a quote for a bottle custom",
    details: [
      { label: "Formats", value: "Unique pieces · series · event series · live personalization" },
      { label: "Applications", value: "Product launch · brand event · gifting · private party · corporate event · wedding · activation · recurring event" },
    ],
  },
  "objets-bijoux": {
    title: "Objects & Jewelry",
    accroche: "Art in the details",
    description: "Artistic personalization of watches, dials, jewelry and precious objects. The creation can be developed from an idea, a pattern, a visual universe or a specific request. For a house, a brand or an individual, each project is studied to adapt the intervention to the medium and the desired identity.",
    cta: "Request a quote for an object or jewel",
    details: [
      { label: "Mediums", value: "Watches · dials · jewelry · precious objects" },
      { label: "Formats", value: "Unique pieces · collaborations · gifting · events" },
      { label: "For", value: "Houses · watchmaking · jewelry · brands · individuals" },
    ],
  },
  "feuille-or": {
    title: "Gold Leaf",
    accroche: "Radiance as an artistic material",
    description: "We create gold leaf interventions to bring a precious, graphic finish to various objects and surfaces. Gilding can be integrated into a painted creation, a motif, or used as the central element of the composition.",
    cta: "Request a quote for a gold leaf finish",
    details: [
      { label: "Mediums", value: "Leather · objects · leather accessories · decorative surfaces · personalized pieces · paper" },
      { label: "Formats", value: "Unique pieces · limited series · personalization · collaborations" },
      { label: "Desired effect", value: "Precious detail · contrast · visual signature · artistic finish" },
    ],
  },
  "peinture-textile": {
    title: "Textile Painting",
    accroche: "Wear a work of art",
    description: "Our artists develop artistic creations directly on textile, from one-of-a-kind pieces to event projects. Evening gowns, jackets, t-shirts, sweatshirts and other garments can be transformed or personalized through a pictorial intervention.",
    cta: "Request a quote for textile painting",
    details: [
      { label: "Formats", value: "Unique piece · bespoke · event · brand collaboration" },
      { label: "For", value: "Fashion · events · artists · brands · individuals · activation" },
      { label: "Highlight", value: "This service walked the red carpet at the Festival de Cannes" },
    ],
  },
  "peinture-murale": {
    title: "Mural Painting",
    accroche: "Give spaces a new dimension",
    description: "We create murals and pictorial interventions on walls, facades and indoor or outdoor spaces. The intervention can be part of an artistic project, the design of a professional space, an event or the creation of a specific visual identity. Each project is developed from the context of the place, its architecture and the desired visual universe.",
    cta: "Request a quote for a mural",
    details: [
      { label: "Spaces", value: "Hotels · restaurants · boutiques · offices · cultural venues · events" },
      { label: "Format", value: "Artistic design · on-site production · bespoke project" },
    ],
  },
  "live-painting": {
    title: "Live Painting",
    accroche: "A live creation",
    description: "An artistic performance created live during your event. A work is created in front of the audience, on canvas or on a medium defined with the client. The performance can be imagined around a theme, a product, a place or a brand's identity.",
    cta: "Request a quote for a live painting",
    details: [
      { label: "Applications", value: "Product launch · brand event · party · activation · hotel · private event" },
      { label: "Format", value: "Medium · dimensions · duration and bespoke art direction" },
    ],
  },
  "workshops": {
    title: "Workshops",
    accroche: "Create, Experiment, Share",
    description: "Participatory art workshops to discover the basics of painting and personalization. Sneakers — discover the basics of customization and personalize your pair. Canvas — explore painting and create an original work. Textile — experiment with painting applied to clothing.",
    cta: "Organize a workshop",
    details: [
      { label: "For", value: "Companies · public sector · cultural institutions · associations · events · private groups" },
      { label: "Formats", value: "Introduction · collaborative workshop · team building · event animation" },
    ],
  },
  "ateliers-collage": {
    title: "Collage Workshops",
    accroche: "Collect, Assemble, Create",
    description: "Workshops designed as spaces for experimentation and collective creation. Each workshop adapts to audiences, contexts and project goals, from introduction to a more immersive artistic experience. A playful and accessible way to create together, develop your eye and give images a new life.",
    cta: "Organize a collage workshop",
    details: [
      { label: "Formats", value: "Animation · team building · children & teens activities · participatory workshops" },
      { label: "For", value: "Public sector · associations · companies · cultural institutions · events" },
    ],
  },
};

export const localizePrestation = (p, lang) =>
  lang === "en" && PRESTATION_EN[p.slug] ? { ...p, ...PRESTATION_EN[p.slug] } : p;

export const MANIFESTO_PARAS_EN = [
  "NEW CREATION studio is a multidisciplinary artistic creation studio that transforms objects, spaces and events into unique visual experiences.",
  "At the crossroads of art, personalization and events, we bring together artists with complementary crafts to imagine and deliver bespoke creations — bold and singular, tailored to each project.",
  "Our experience has led us to collaborate with major players such as Adidas, POSCA, Galeries Lafayette Paris, La Samaritaine, Printemps, Citadium, JD, W Hotels, Le Royal Monceau – Raffles Paris, Quai 54, Palais des Congrès de Paris, BRED Banque Populaire, Fouquet's Paris and the Festival de Cannes.",
  "With more than 5,000 personalization projects completed, along with numerous murals, artworks and artistic experiences, NEW CREATION puts creation at the service of projects that want to leave a mark, create engagement and give their image a new dimension.",
];

const STRINGS = {
  "nav.studio": { fr: "Le Studio", en: "The Studio" },
  "nav.prestations": { fr: "Prestations", en: "Services" },
  "nav.galerie": { fr: "Galerie", en: "Gallery" },
  "nav.contact": { fr: "Contact", en: "Contact" },
  "nav.reserve": { fr: "Réserver", en: "Book now" },

  "hero.baseline": { fr: "Créations · Performances · Expériences", en: "Creations · Performances · Experiences" },
  "hero.subtitle": { fr: "Votre projet. Notre créativité. Une création unique.", en: "Your project. Our creativity. A one-of-a-kind creation." },
  "hero.cta1": { fr: "Découvrir nos prestations", en: "Discover our services" },
  "hero.cta2": { fr: "Demander un devis", en: "Request a quote" },

  "ov.studio": { fr: "Le studio", en: "The studio" },
  "ov.prestations": { fr: "Les prestations", en: "Services" },
  "ov.pourqui": { fr: "Pour qui", en: "Who for" },
  "ov.gallery": { fr: "Nos créations", en: "Our creations" },
  "ov.contact": { fr: "Contact", en: "Contact" },
  "ov.galerie": { fr: "Galerie", en: "Gallery" },
  "ov.references": { fr: "Références", en: "References" },
  "ov.team": { fr: "L'équipe", en: "The team" },
  "ov.manifeste": { fr: "Le manifeste", en: "The manifesto" },
  "ov.galleryDedic": { fr: "Galerie dédiée", en: "Dedicated gallery" },

  "home.teaserH2a": { fr: "Votre projet, notre créativité,", en: "Your project, our creativity," },
  "home.teaserH2b": { fr: "une création unique", en: "a one-of-a-kind creation" },
  "home.teaserP1": { fr: "NEW CREATION studio est un studio de création artistique pluridisciplinaire qui transforme les objets, les espaces et les événements en expériences visuelles uniques.", en: "NEW CREATION studio is a multidisciplinary artistic creation studio that transforms objects, spaces and events into unique visual experiences." },
  "home.teaserP2": { fr: "À la croisée de l'art, de la personnalisation et de l'événementiel, nous réunissons des artistes aux savoir-faire complémentaires pour imaginer et réaliser des créations sur mesure, fortes et singulières.", en: "At the crossroads of art, personalization and events, we bring together artists with complementary crafts to imagine and deliver bespoke, bold and singular creations." },
  "home.teaserLink": { fr: "Découvrir le studio", en: "Discover the studio" },
  "home.stat1": { fr: "projets de personnalisation réalisés", en: "personalization projects completed" },
  "home.stat2": { fr: "marques et institutions accompagnées", en: "brands and institutions supported" },
  "home.polesH2": { fr: "Trois pôles, neuf savoir-faire", en: "Three pillars, nine crafts" },
  "home.polesIntro": { fr: "De la customisation à la peinture, des fresques murales au live painting et aux ateliers créatifs, notre approche s'adapte aux univers des marques, entreprises, institutions, lieux culturels et événements.", en: "From customization to painting, from murals to live painting and creative workshops, our approach adapts to the worlds of brands, companies, institutions, cultural venues and events." },
  "home.polesCta": { fr: "Voir toutes les prestations", en: "See all services" },
  "home.pourquiH2": { fr: "Un art pour chaque lieu", en: "Art for every place" },
  "home.galleryH2": { fr: "Œuvres & performances", en: "Works & performances" },
  "home.galleryCta": { fr: "Voir toute la galerie", en: "View the full gallery" },

  "aud1.t": { fr: "Marques & boutiques", en: "Brands & boutiques" },
  "aud1.x": { fr: "Activations produit, personnalisation en boutique, collaborations exclusives.", en: "Product activations, in-store personalization, exclusive collaborations." },
  "aud2.t": { fr: "Hôtels & lieux culturels", en: "Hotels & cultural venues" },
  "aud2.x": { fr: "Fresques, identité visuelle, œuvres pérennes ou événementielles.", en: "Murals, visual identity, permanent or event-based artworks." },
  "aud3.t": { fr: "Entreprises & collectivités", en: "Companies & public sector" },
  "aud3.x": { fr: "Team building créatif, ateliers, animations lors d'événements corporate.", en: "Creative team building, workshops, entertainment at corporate events." },
  "aud4.t": { fr: "Particuliers & mariages", en: "Individuals & weddings" },
  "aud4.x": { fr: "Pièces uniques, cadeaux personnalisés, moments artistiques mémorables.", en: "Unique pieces, personalized gifts, memorable artistic moments." },

  "refs.artistsH3": { fr: "Ils ont porté nos créations", en: "They have worn our creations" },
  "refs.logosH2": { fr: "Ils nous ont fait confiance", en: "They trusted us" },

  "cb.h2": { fr: "Parlons de votre projet", en: "Let's talk about your project" },
  "cb.lead": { fr: "Une idée, une envie, un événement ? Créons quelque chose d'unique.", en: "An idea, a desire, an event? Let's create something unique." },
  "cb.para": { fr: "Vous souhaitez personnaliser une pièce, créer une œuvre, imaginer une performance ou proposer une expérience artistique pour votre événement ? NEW CREATION studio conçoit des formats sur mesure, pensés selon votre univers, votre public et vos objectifs.", en: "Want to personalize a piece, create an artwork, imagine a performance or offer an artistic experience for your event? NEW CREATION studio designs bespoke formats, crafted around your universe, your audience and your goals." },
  "cb.tab1": { fr: "Demande de devis", en: "Quote request" },
  "cb.tab2": { fr: "Réserver un créneau", en: "Book a slot" },

  "f.name": { fr: "Nom / Société", en: "Name / Company" },
  "f.namePh": { fr: "Votre nom ou société", en: "Your name or company" },
  "f.email": { fr: "Email", en: "Email" },
  "f.phone": { fr: "Téléphone", en: "Phone" },
  "f.phoneOpt": { fr: "Téléphone (optionnel)", en: "Phone (optional)" },
  "f.youAre": { fr: "Vous êtes", en: "You are" },
  "f.whichService": { fr: "Quelle prestation vous intéresse ?", en: "Which service are you interested in?" },
  "f.project": { fr: "Décrivez votre projet", en: "Describe your project" },
  "f.projectPh": { fr: "Votre événement, vos envies, vos contraintes…", en: "Your event, your wishes, your constraints…" },
  "f.budget": { fr: "Budget estimé (optionnel)", en: "Estimated budget (optional)" },
  "f.budgetPh": { fr: "Ex : 2 000 – 5 000 €", en: "E.g. €2,000 – €5,000" },
  "f.date": { fr: "Date souhaitée (optionnel)", en: "Desired date (optional)" },
  "f.send": { fr: "Envoyer ma demande", en: "Send my request" },
  "f.sending": { fr: "Envoi…", en: "Sending…" },
  "f.selectError": { fr: "Sélectionnez votre profil et la prestation souhaitée.", en: "Please select your profile and the desired service." },
  "f.success": { fr: "Votre demande a bien été envoyée. Le studio vous répondra très vite.", en: "Your request has been sent. The studio will get back to you very soon." },
  "f.dateLabel": { fr: "Date souhaitée", en: "Preferred date" },
  "f.slot": { fr: "Créneau", en: "Time slot" },
  "f.pickDate": { fr: "Sélectionnez d'abord une date.", en: "Select a date first." },
  "f.prestation": { fr: "Prestation", en: "Service" },
  "f.firstName": { fr: "Nom", en: "Name" },
  "f.yourName": { fr: "Votre nom", en: "Your name" },
  "f.details": { fr: "Précisions (optionnel)", en: "Details (optional)" },
  "f.detailsPh": { fr: "Lieu, nombre d'invités, durée…", en: "Location, number of guests, duration…" },
  "f.book": { fr: "Réserver ce créneau", en: "Book this slot" },
  "f.booking": { fr: "Réservation…", en: "Booking…" },
  "f.bookError": { fr: "Choisissez une date, un créneau et une prestation.", en: "Please choose a date, a slot and a service." },
  "f.bookSuccess": { fr: "Créneau réservé ! Vous recevrez une confirmation par email.", en: "Slot booked! You will receive a confirmation by email." },

  "st.h1a": { fr: "Votre projet. Notre créativité.", en: "Your project. Our creativity." },
  "st.h1b": { fr: "Une création unique.", en: "A one-of-a-kind creation." },
  "st.fig1": { fr: "projets de personnalisation réalisés", en: "personalization projects completed" },
  "st.fig2": { fr: "disciplines artistiques maîtrisées", en: "artistic disciplines mastered" },
  "st.fig3": { fr: "marques et institutions accompagnées", en: "brands and institutions supported" },
  "st.fig4": { fr: "basé, disponible partout en France", en: "based, available across France" },
  "st.teamH2": { fr: "Deux regards, un studio", en: "Two visions, one studio" },
  "st.role1": { fr: "Directeur créatif", en: "Creative Director" },
  "st.role2": { fr: "Directrice artistique", en: "Artistic Director" },
  "st.bio1": { fr: "Peintre et performeur, il signe le geste et la couleur du studio : customisation, fresques et live painting.", en: "Painter and performer, he signs the studio's gesture and color: customization, murals and live painting." },
  "st.bio2": { fr: "Elle orchestre l'univers visuel du studio : direction artistique, scénographie et ateliers de transmission.", en: "She orchestrates the studio's visual universe: art direction, scenography and transmission workshops." },
  "st.ctaH2": { fr: "Envie de créer avec nous ?", en: "Want to create with us?" },
  "st.cta2": { fr: "Nous contacter", en: "Contact us" },

  "pr.h1": { fr: "Personnaliser · Peindre · Transmettre", en: "Customize · Paint · Share" },
  "pr.intro": { fr: "De la customisation à la peinture, des fresques murales au live painting et aux ateliers créatifs, notre approche s'adapte aux univers des marques, entreprises, institutions, lieux culturels et événements.", en: "From customization to painting, from murals to live painting and creative workshops, our approach adapts to the worlds of brands, companies, institutions, cultural venues and events." },
  "pr.card": { fr: "Voir la fiche", en: "View details" },
  "pr.bottomH2": { fr: "Un projet qui ne rentre dans aucune case ?", en: "A project that doesn't fit any box?" },
  "pr.bottomP": { fr: "Nous concevons aussi des formats sur mesure.", en: "We also design custom formats." },
  "pr.bottomCta": { fr: "Parlons de votre projet", en: "Let's talk about your project" },
  "pr.back": { fr: "Prestations", en: "Services" },
  "pr.works": { fr: "Réalisations", en: "Works" },
  "pr.empty": { fr: "Les réalisations de cette prestation arrivent très bientôt dans la galerie.", en: "Works for this service are coming soon to the gallery." },
  "pr.notFound": { fr: "Prestation introuvable", en: "Service not found" },
  "pr.all": { fr: "Toutes les prestations", en: "All services" },
  "pr.defaultCta": { fr: "Demander un devis pour cette prestation", en: "Request a quote for this service" },

  "ga.h1": { fr: "Nos créations", en: "Our creations" },
  "ga.sub": { fr: "Plus de 5 000 pièces personnalisées, fresques et performances — un aperçu de notre univers. Survolez une vidéo pour la voir prendre vie.", en: "Over 5,000 personalized pieces, murals and performances — a glimpse into our universe. Hover over a video to see it come alive." },
  "ga.all": { fr: "Tout", en: "All" },
  "ga.photos": { fr: "Photos", en: "Photos" },
  "ga.videos": { fr: "Vidéos", en: "Videos" },
  "ga.allServ": { fr: "Toutes prestations", en: "All services" },
  "ga.empty": { fr: "Aucune œuvre dans cette catégorie pour le moment.", en: "No work in this category yet." },
  "ga.note": { fr: "Sélection d'images d'ambiance — les œuvres réelles du studio arrivent très bientôt.", en: "A selection of mood images — the studio's real works are coming soon." },

  "ct.h1": { fr: "Parlons de votre projet", en: "Let's talk about your project" },
  "ct.para": { fr: "Vous souhaitez personnaliser une pièce, créer une œuvre, imaginer une performance ou proposer une expérience artistique pour votre événement ? NEW CREATION studio conçoit des formats sur mesure, pensés selon votre univers, votre public et vos objectifs. Parlons de votre projet et imaginons ensemble sa prochaine création.", en: "Want to personalize a piece, create an artwork, imagine a performance or offer an artistic experience for your event? NEW CREATION studio designs bespoke formats, crafted around your universe, your audience and your goals. Let's talk about your project and imagine its next creation together." },
  "ct.team": { fr: "La direction", en: "The team" },

  "ft.tagline": { fr: "Studio de création artistique pluridisciplinaire. Customisation, fresques murales, live painting et ateliers — des expériences visuelles uniques.", en: "A multidisciplinary artistic creation studio. Customization, murals, live painting and workshops — unique visual experiences." },
  "ft.nav": { fr: "Navigation", en: "Navigation" },
  "ft.team": { fr: "La direction", en: "The team" },
  "ft.admin": { fr: "Espace artiste", en: "Artist area" },
  "ft.giant": { fr: "Créons ensemble", en: "Let's create together" },
  "ft.bottom": { fr: "Créations · Performances · Expériences — Paris · France", en: "Creations · Performances · Experiences — Paris · France" },

  "ch.title": { fr: "Assistant créatif", en: "Creative assistant" },
  "ch.powered": { fr: "Propulsé par Claude", en: "Powered by Claude" },
  "ch.welcome": { fr: "Bonjour ! Je suis l'assistant créatif du studio. Parlez-moi de votre projet — une pièce à personnaliser, un mur à transformer, un événement à sublimer — et imaginons-le ensemble.", en: "Hello! I'm the studio's creative assistant. Tell me about your project — a piece to personalize, a wall to transform, an event to elevate — and let's imagine it together." },
  "ch.s1": { fr: "Customiser des sneakers pour un événement", en: "Customize sneakers for an event" },
  "ch.s2": { fr: "Organiser un live painting", en: "Organize a live painting" },
  "ch.s3": { fr: "Un workshop pour mon équipe", en: "A workshop for my team" },
  "ch.inputPh": { fr: "Décrivez votre idée…", en: "Describe your idea…" },
  "ch.devis": { fr: "Demander un devis", en: "Request a quote" },
  "ch.error": { fr: "Une petite panne d'inspiration… Réessayez dans un instant, ou écrivez-nous directement via la page contact.", en: "A brief creative block… Please try again in a moment, or write to us directly via the contact page." },
  "ch.resumePrefix": { fr: "Échange avec l'assistant : ", en: "Chat with the assistant: " },
};
